import type { ParentType as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { useGetOversightResultsByParentId } from 'src/hooks/queries/assessment-result/useGetOversightResultsByParentId';

import { getFriendlyId } from '@/utils/friendlyId';

import type {
  OversightResultFields,
  OversightResultPresentationFields,
} from './types';

type GetOversightResultsByParentIdData = NonNullable<
  ReturnType<typeof useGetOversightResultsByParentId>['data']
>;

export const getParentTitle = (record: OversightResultFields) => {
  switch (record.typename) {
    case 'document_oversight_result':
      return (
        (
          record as Partial<
            Omit<
              GetOversightResultsByParentIdData['document_oversight_result'][0],
              'files'
            >
          >
        ).parents?.[0]?.document?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'obligation_oversight_result':
      return (
        (
          record as Partial<
            Omit<
              GetOversightResultsByParentIdData['obligation_oversight_result'][0],
              'files'
            >
          >
        ).parents?.[0]?.obligation?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'risk_controlled_oversight_result':
      return (
        (
          record as Partial<
            Omit<
              GetOversightResultsByParentIdData['risk_controlled_oversight_result'][0],
              'files'
            >
          >
        ).parents?.[0]?.risk?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'risk_uncontrolled_oversight_result':
      return (
        (
          record as Partial<
            Omit<
              GetOversightResultsByParentIdData['risk_uncontrolled_oversight_result'][0],
              'files'
            >
          >
        ).parents?.[0]?.risk?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'control_test_oversight_result':
      return (
        (
          record as Partial<
            Omit<
              GetOversightResultsByParentIdData['control_test_oversight_result'][0],
              'files'
            >
          >
        )?.parent?.Title ?? '-'
      );
  }
};

export const decorateWithControlType = (
  value: string,
  getControlTypeLabel: (
    controlType: Risk_Assessment_Result_Control_Type_Enum
  ) => string,
  record: OversightResultPresentationFields
) => {
  if (record.typename === 'risk_controlled_oversight_result') {
    return `${value} - ${getControlTypeLabel(Risk_Assessment_Result_Control_Type_Enum.Controlled)}`;
  }

  if (record.typename === 'risk_uncontrolled_oversight_result') {
    return `${value} - ${getControlTypeLabel(Risk_Assessment_Result_Control_Type_Enum.Uncontrolled)}`;
  }

  return value;
};
